import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { InfoCircleIcon } from "@cmsgov/ds-medicare-gov";
import { ActionType, IRAInsulinCoachmarkType } from "../../@types";
import { AppContext } from "../../app/store";
import AlertMessage from "../AlertMessage/AlertMessage";
import { AlertMessageProps } from "../AlertMessage/AlertMessage";
import { displayDrug, useTranslate } from "../../helpers";
import {
  AnalyticsActionType,
  Ga4Event,
  Ga4EventType,
  Ga4ParentComponentType,
} from "../../app/contexts/Analytics/types";
import classNames from "classnames";
import { FormattedMessage } from "react-intl";

export const insulinDrugCoachmarkTestid = "insulin-drug-coachmark";
export const insulinCoachmarkTestId = "insulin-coachmark";

interface InsulinCoachmarkProps
  extends Omit<AlertMessageProps, "icon" | "onClose" | "body" | "title"> {
  body?: AlertMessageProps["body"];
  title?: AlertMessageProps["title"];
  coachmarkType: IRAInsulinCoachmarkType;
  suppressCoachmark?: boolean;
}
export const InsulinCoachmark: FunctionComponent<InsulinCoachmarkProps> = ({
  coachmarkType,
  className,
  dataTestId,
  body,
  title,
  suppressCoachmark = false,
  ...remainingProps
}: InsulinCoachmarkProps) => {
  const {
    state,
    state: { prescriptions, trackedEvents },
    dispatch,
  } = useContext(AppContext);
  const [insulinDrugList, setInsulinDrugList] = useState<string[]>([]);
  const flags = useFlags();
  const t = useTranslate();
  const isDrugListCoachmark =
    coachmarkType === IRAInsulinCoachmarkType.DRUG_LIST;
  let testId = dataTestId;
  if (!testId) {
    testId = isDrugListCoachmark
      ? insulinDrugCoachmarkTestid
      : insulinCoachmarkTestId;
  }
  let coachmarkTitle = title;
  let coachmarkBody = body;

  if (isDrugListCoachmark) {
    coachmarkTitle = t("coachmark.insulin.drug.title");
    coachmarkBody = (
      <ul>
        <li>{t("coachmark.insulin.drug.body.list.one")}</li>
        <FormattedMessage
          id="coachmark.insulin.drug.body.list.two"
          tagName="li"
          values={{
            strong: (chunks: string[]) => <strong>{chunks}</strong>,
          }}
        />
        <li>{t("coachmark.insulin.drug.body.list.three")}</li>
      </ul>
    );
  }

  useEffect(() => {
    const trackedIraInsulinImpressions =
      trackedEvents[Ga4Event.IRA_INSULIN_IMPRESSION] || {};
    const insulinDrugs = prescriptions.filter(drug => drug.insulin);
    const drugsNotTrackedMap = {};

    if (insulinDrugs.length > 0) {
      insulinDrugs.forEach(drug => {
        const drugName = displayDrug(t, drug);
        if (!trackedIraInsulinImpressions[drugName]) {
          drugsNotTrackedMap[drugName] = true;
        }
      });
      const drugsNotTracked = Object.keys(drugsNotTrackedMap);
      if (drugsNotTracked.length > 0) {
        const drugNames = drugsNotTracked.join(",");
        dispatch({
          type: AnalyticsActionType.SEND_GA4_EVENT,
          settings: {
            event_name: Ga4Event.IRA_INSULIN_IMPRESSION,
            event_type: Ga4EventType.NON_INTERACTION,
            mct_insulin_drug_name: drugNames,
            parent_component_heading: "ira coach mark",
            parent_component_type: Ga4ParentComponentType.ALERT,
          },
        });
        dispatch({
          type: AnalyticsActionType.SEND_TEALIUM_EVENT,
          settings: {
            event_action: "ira insulin impression",
            event_label: drugNames,
          },
        });
        dispatch({
          type: ActionType.EVENT_TRACKED,
          payload: {
            event: Ga4Event.IRA_INSULIN_IMPRESSION,
            value: {
              ...trackedIraInsulinImpressions,
              ...drugsNotTrackedMap,
            },
          },
        });
      }
    }
    setInsulinDrugList(insulinDrugs.map(drug => displayDrug(t, drug)));
  }, [prescriptions]);

  const handleOnClose = () => {
    dispatch({
      type: ActionType.UPDATE_IRA_INSULIN_COACHMARK_HIDDEN_STATES,
      payload: coachmarkType,
    });
    dispatch({
      type: AnalyticsActionType.SEND_GA4_EVENT,
      settings: {
        event_name: Ga4Event.IRA_INSULIN_ALERT_CLOSED,
        event_type: Ga4EventType.UI_INTERACTION,
        mct_insulin_drug_name: insulinDrugList.join(","),
        parent_component_heading: "ira coach mark",
        parent_component_type: Ga4ParentComponentType.ALERT,
      },
    });
    dispatch({
      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
      settings: {
        event_action: "ira insulin alert closed",
        event_label: insulinDrugList.join(","),
      },
    });
  };

  const showInsulinCoachmark =
    insulinDrugList.length > 0 &&
    flags.tmpFeEnableIraInsulinCoachmarks &&
    !suppressCoachmark &&
    !state.iraInsulinCoachmarkHiddenStates[coachmarkType];

  return showInsulinCoachmark ? (
    <AlertMessage
      icon={<InfoCircleIcon />}
      className={classNames(className, {
        "mct-c-insulin-drug-coachmark": isDrugListCoachmark,
      })}
      title={coachmarkTitle}
      body={coachmarkBody}
      dataTestId={testId}
      onClose={handleOnClose}
      {...remainingProps}
    />
  ) : null;
};
